import { useMutation } from "@tanstack/react-query";

import {
  caaCustomerAddressCreateMutation,
  caaCustomerAddressDeleteMutation,
  caaCustomerAddressUpdateMutation,
  caaCustomerEmailMarketingSubscribeMutation,
  caaCustomerEmailMarketingUnsubscribeMutation,
  caaCustomerQuery,
  caaCustomerUpdateMutation,
} from "../graphql/customer-account-gql";
import type {
  ICaaCustomerAddressCreateMutation,
  ICaaCustomerAddressDeleteMutation,
  ICaaCustomerAddressUpdateMutation,
  ICaaCustomerEmailMarketingSubscribeMutation,
  ICaaCustomerEmailMarketingUnsubscribeMutation,
  ICaaCustomerQuery,
  ICaaCustomerUpdateMutation,
  ICaaUserError,
} from "../interface/customer-account";
import { locale } from "../locales";
import type { TCaaRequestFn } from "../types/graphql";
import type { IToast } from "../types/layout";
import { ToastType } from "../types/layout";
import { customerErrorHandler } from "../utils/errorHandler";

interface IUseCustomerQueryCaaProps {
  request: TCaaRequestFn;
  lang: "ko" | "en" | "jp";
  openToast: (toast: IToast) => void;
}

/**
 * Customer Account API customer 쿼리/뮤테이션 바인딩
 */
export default function useCustomerQueryCaa({ request, lang, openToast }: IUseCustomerQueryCaaProps) {
  const notifyServerError = () => {
    openToast({ message: locale(lang).error.server, type: ToastType.Error });
  };

  const notifyUserErrors = (userErrors: ICaaUserError[]) => {
    userErrors.forEach((error) => {
      openToast({ type: ToastType.Error, message: customerErrorHandler(error.code, lang) });
    });
  };

  const fetchCaaCustomerMutation = useMutation({
    mutationFn: () => request<ICaaCustomerQuery>(caaCustomerQuery),
  });

  const updateCaaCustomerMutation = useMutation({
    mutationFn: (variables: Record<string, unknown>) => request<ICaaCustomerUpdateMutation>(caaCustomerUpdateMutation, variables),
    onSuccess: (data) => {
      notifyUserErrors(data.customerUpdate.userErrors);
    },
    onError: notifyServerError,
  });

  const createCaaAddressMutation = useMutation({
    mutationFn: (variables: Record<string, unknown>) =>
      request<ICaaCustomerAddressCreateMutation>(caaCustomerAddressCreateMutation, variables),
    onSuccess: (data) => {
      notifyUserErrors(data.customerAddressCreate.userErrors);
    },
    onError: notifyServerError,
  });

  const editCaaAddressMutation = useMutation({
    mutationFn: (variables: Record<string, unknown>) =>
      request<ICaaCustomerAddressUpdateMutation>(caaCustomerAddressUpdateMutation, variables),
    onSuccess: (data) => {
      notifyUserErrors(data.customerAddressUpdate.userErrors);
    },
    onError: notifyServerError,
  });

  const deleteCaaAddressMutation = useMutation({
    mutationFn: (variables: Record<string, unknown>) =>
      request<ICaaCustomerAddressDeleteMutation>(caaCustomerAddressDeleteMutation, variables),
    onSuccess: (data) => {
      notifyUserErrors(data.customerAddressDelete.userErrors);
    },
    onError: notifyServerError,
  });

  const subscribeCaaEmailMarketingMutation = useMutation({
    mutationFn: (variables: Record<string, unknown>) =>
      request<ICaaCustomerEmailMarketingSubscribeMutation>(caaCustomerEmailMarketingSubscribeMutation, variables),
    onSuccess: (data) => {
      notifyUserErrors(data.customerEmailMarketingSubscribe.userErrors);
    },
    onError: notifyServerError,
  });

  const unsubscribeCaaEmailMarketingMutation = useMutation({
    mutationFn: (variables: Record<string, unknown>) =>
      request<ICaaCustomerEmailMarketingUnsubscribeMutation>(caaCustomerEmailMarketingUnsubscribeMutation, variables),
    onSuccess: (data) => {
      notifyUserErrors(data.customerEmailMarketingUnsubscribe.userErrors);
    },
    onError: notifyServerError,
  });

  return {
    fetchCaaCustomer: fetchCaaCustomerMutation.mutateAsync,
    fetchCaaCustomerLoading: fetchCaaCustomerMutation.isPending,
    updateCaaCustomer: updateCaaCustomerMutation.mutateAsync,
    updateCaaCustomerLoading: updateCaaCustomerMutation.isPending,
    createCaaAddress: createCaaAddressMutation.mutateAsync,
    createCaaAddressLoading: createCaaAddressMutation.isPending,
    editCaaAddress: editCaaAddressMutation.mutateAsync,
    editCaaAddressLoading: editCaaAddressMutation.isPending,
    deleteCaaAddress: deleteCaaAddressMutation.mutateAsync,
    deleteCaaAddressLoading: deleteCaaAddressMutation.isPending,
    subscribeCaaEmailMarketing: subscribeCaaEmailMarketingMutation.mutateAsync,
    subscribeCaaEmailMarketingLoading: subscribeCaaEmailMarketingMutation.isPending,
    unsubscribeCaaEmailMarketing: unsubscribeCaaEmailMarketingMutation.mutateAsync,
    unsubscribeCaaEmailMarketingLoading: unsubscribeCaaEmailMarketingMutation.isPending,
  };
}
